<template>
    <div class="new-broadcast-form">
        <a-steps v-model="current" type="navigation" size="small" :style="stepStyle">
            <a-step
                :title="$t('wp.broadcast.detail')"
                :description="$t('wp.broadcastTitle')"
                :status="current === 0 ? 'process' : 'wait'"
            />
            <a-step
                :title="$t('wp.recipients')"
                :description="$t('wp.selectRecipients')"
                :status="current === 1 ? 'process' : 'wait'"
            />
            <a-step
                :title="$t('wp.messages')"
                :description="$t('wp.writeYourMessage')"
                :status="current === 2 ? 'process' : 'wait'"
            />
            <a-step
                :title="$t('wp.schedule')"
                :description="$t('wp.scheduleNowOrLater')"
                :status="current === 3 ? 'process' : 'wait'"
            />
            <a-step
                :title="$t('wp.summary')"
                :description="$t('wp.summary')"
                :status="current === 4 ? 'process' : 'wait'"
            />
        </a-steps>
        <div v-if="current === 0">
            <md-field>
                <label>{{ $t("wp.title") }}</label>
                <md-input v-model="model.title" required></md-input>
            </md-field>
            <md-field>
                <label>{{ $t("wp.description") }}</label>
                <md-textarea v-model="model.description" required></md-textarea>
            </md-field>
        </div>
        <div v-if="current === 1">
            <RecipientSelect v-model="model" :contact-list="contacts" :channel="channel" />
        </div>
        <div v-if="current === 2">
            <MetaTemplateMessageSelector :channel="channel" v-model="model" v-if="isMetaWabaChannel" />
            <TemplateMessageSelector :channel-id="channelId" v-model="model" v-else-if="isWhatsappBusinessChannel" />
            <MessageEditor v-model="model.messages" :attributes="getAttributes()" v-else />
        </div>

        <div v-if="current === 3">
            <TimeSelect v-model="model" />
        </div>
        <div v-if="current === 4">
            <Summary
                :model="model"
                :contacts="contacts"
                :loading="loading"
                :submit="submit"
                :cancel="cancel"
                :channel="channel"
            />
        </div>
        <a-divider class="=mt-5" />
        <div class="navs">
            <div>
                <md-button class="md-raised md-primary" @click="goPrev" v-if="current > 0">
                    <div class="d-flex align-items-center justify-content-center px-3">
                        <span class="material-icons mr-3"> arrow_back </span>
                        <span v-if="current === 1">{{ $t("wp.broadcast.detail") }}</span>
                        <span v-if="current === 2">{{ $t("wp.recipients") }}</span>
                        <span v-if="current === 3">{{ $t("wp.messages") }}</span>
                        <span v-if="current === 4">{{ $t("wp.schedule") }}</span>
                    </div>
                </md-button>
            </div>
            <div>
                <md-button class="md-raised md-secondary" @click="cancel">
                    <div class="d-flex align-items-center justify-content-center px-3">{{ $t("wp.cancel") }}</div>
                </md-button>
                <md-button class="md-raised md-primary" @click="goNext" v-if="current < 4">
                    <div class="d-flex align-items-center justify-content-center px-3">
                        <span class="material-icons mr-3"> arrow_forward </span>
                        <span v-if="current === 0">{{ $t("wp.recipients") }}</span>
                        <span v-if="current === 1">{{ $t("wp.messages") }}</span>
                        <span v-if="current === 2">{{ $t("wp.schedule") }}</span>
                        <span v-if="current === 3">{{ $t("wp.summary") }}</span>
                    </div>
                </md-button>
                <md-button class="md-raised md-primary" :disabled="loading" @click="submit" v-if="current === 4">
                    <div class="d-flex align-items-center justify-content-center px-3">
                        <span class="material-icons mr-3"> send </span>
                        {{ loading ? $t("wp.sending") : $t("wp.send") }}
                    </div>
                </md-button>
            </div>
        </div>
    </div>
</template>

<script>
import "./broadcast.scss";
import MessageEditor from "./MessageEditor";
import { generateRandomId } from "../../../../../../utils/session";
import { mapState } from "vuex";
import { sendSocketMessage } from "../../../../../../utils/socketStream";
import RecipientSelect from "./RecipientSelect";
import TimeSelect from "./TimeSelect";
import Summary from "./Summary";
import WHATSAPP_API from "../../../../../../api/whatsapp";
import log2 from "../../../../../../utils/log2";
import TemplateMessageSelector from "./TemplateMessageSelector.vue";
import MetaTemplateMessageSelector from "./MetaTemplateMessageSelector.vue";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";

const log = log2("NewBroadcastMessage");
export default {
    name: "NewBroadcastMessage",
    components: { 
        TemplateMessageSelector, 
        MetaTemplateMessageSelector, 
        MessageEditor, 
        RecipientSelect, 
        TimeSelect, 
        Summary 
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            isAdmin: (state) => state.auth.adminUser != null,
            site: (state) => state.site.site,
            contacts: (state) => {
                const numbers = state.whatsapp.contacts;
                if (Array.isArray(numbers)) {
                    return numbers.filter((x) => x.id.includes("@c") && !!x.name);
                }
                return [];
            },
        }),
    },
    props: {
        onSuccess: {
            type: Function,
            required: true,
        },
        onCancel: {
            type: Function,
            required: true,
        },
        channel: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            current: 0,
            stepStyle: {
                marginBottom: "60px",
                boxShadow: "0px -1px 0 0 #e8e8e8 inset",
            },
            loading: false,
            channelId: this.channel?._id,
            isMetaWabaChannel: this.channel?.type === "whatsapp-business-api-supsis",
            isWhatsappBusinessChannel: ["whatsapp-business-api", "whatsapp-business-api-supsis"].includes(this.channel?.type),
            model: {
                title: "",
                description: "",
                messages: [
                    {
                        id: generateRandomId(),
                        type: "TEXT",
                        delay: 2,
                        text: "Merhaba {{name}},bizi tercih ettiğiniz için teşekkür ederiz.", //attribute may contains
                        filters: {
                            operator: "or",
                            items: [], ////{rule,attribute,value}
                        },
                    },
                ],
                all: !this.channel,
                groupId: null,
                customNumbers: [],
                excludePhoneNumbers: [],
                sendNow: true,
                sendFutureAt: null,
                channelId: this.channel?._id,
                selectedTemplate: null,
                templateParams: [],
            },
        };
    },
    mounted() {
        sendSocketMessage("whatsapp get-contacts", { siteId: this.site._id });
    },
    methods: {
        getAttributes() {
            return this.model.all ? ["name"] : Object.keys(_.get(this.model.customNumbers, "[0].attributes", {}));
        },
        goPrev() {
            this.current = Math.max(0, this.current - 1);
        },
        goNext() {
            if (this.current === 0) {
                if (!this.model.title || !this.model.title.trim()) {
                    this.$message.warning(this.$t("wp.title") + " alanı zorunludur.");
                    return;
                }
                if (!this.model.description || !this.model.description.trim()) {
                    this.$message.warning(this.$t("wp.description") + " alanı zorunludur.");
                    return;
                }
            }
            this.current = Math.min(4, this.current + 1);
        },
        submit() {
            this.loading = true;
            const isWhatsappBusinessAPI = ["whatsapp-business-api", "whatsapp-business-api-supsis"].includes(this.channel?.type);
            const messages = isWhatsappBusinessAPI ? [] : this.model.messages;
            WHATSAPP_API.BROADCAST.createBroadcastMessage(
                this.model.title,
                this.model.description,
                messages,
                this.model.all,
                this.model.groupId,
                this.model.excludePhoneNumbers,
                this.user._id,
                this.model.sendFutureAt,
                this.model.customNumbers,
                this.model.channelId,
                this.model.selectedTemplate,
                this.model.templateParams
            )
                .then((res) => {
                    const message = res.data;
                    this.onSuccess(message);
                    this.$message.success("Broadcast created successfully.");
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.BROADCAST_CREATED, message);
                })
                .catch((err) => {
                    this.$message.error("Broadcast could not be created.");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        cancel() {
            this.onCancel();
        },
    },
    watch: {
        model: {
            deep: true,
            handler(model) {
                log("model changed", model);
            },
        },
    },
};
</script>

<style scoped lang="scss">
.navs {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
